import { useNavigate } from 'react-router-dom';
import { BinaryQuestionTemplate } from '@templates';
import { useState } from 'react';
import { ErrorMessages } from '@types';
import { booleanSchema, parseZodErrors } from '@utils';
import { ZodError } from 'zod';
import { emailConfirmationQuestionData } from '@animal-nuisance/constants/questionData';
import { RoutesList } from '@animal-nuisance/routes/Routes';
import { isBinaryOptionDefault } from '@shared/utils';
import { BinaryOptionType } from '@shared/types';
import { useAnimalNuisanceStore } from '@animal-nuisance/hooks/useAnimalNuisanceStore';

export const ConfirmationEmailPage = () => {
  const navigate = useNavigate();
  const {
    data: { wantConfirmationEmail, caseReferenceId },
    update,
  } = useAnimalNuisanceStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const questionData = emailConfirmationQuestionData;
  const errorText = 'Please specify if you want a confirmation email';

  const options = questionData.options.map((option) => ({
    ...option,
    id: option.labelText,
    name: option.labelText,
    isLabelBold: false,
    isDefault: isBinaryOptionDefault(
      option.labelText as BinaryOptionType,
      wantConfirmationEmail,
    ),
  }));

  const handleChange = (result: string) => {
    setErrorMessages([]);
    update('wantConfirmationEmail', result === 'Yes');
  };

  const handleNext = async () => {
    try {
      const validatedData = booleanSchema(questionData.key, errorText).parse({
        [questionData.key]: wantConfirmationEmail,
      });
      update('wantConfirmationEmail', validatedData[questionData.key]);
      if (validatedData[questionData.key] === false) {
        if (caseReferenceId) {
          setErrorMessages([
            {
              path: 'wantConfirmationEmail',
              message:
                'You have already submitted this report. Please return to the homepage.',
            },
          ]);
          return;
        }
      }
      navigate(RoutesList.CONTACT_ABOUT_THE_ISSUE);
    } catch (error) {
      if (error instanceof ZodError) {
        const validationErrors = parseZodErrors(error as ZodError);
        setErrorMessages(
          validationErrors.map((error) => ({
            message: errorText,
            path: error.path,
          })),
        );
      } else {
        setErrorMessages([
          {
            message: (error as Error).message,
            path: questionData.key,
          },
        ]);
      }
    }
  };

  const buttonNext = {
    onClick: handleNext,
  };

  const buttonPrevious = {
    onClick: () => {
      navigate(RoutesList.ISSUE_LOCATION);
    },
  };

  return (
    <BinaryQuestionTemplate
      title={questionData.title}
      errorPath={questionData.key}
      errorMessages={errorMessages}
      options={options}
      onChange={handleChange}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
    />
  );
};
